<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="520"
    content-class="add-quality-grade-modal"
    @click:outside="close"
  >
    <v-card>
      <v-toolbar dark color="success" flat dense>
        <v-toolbar-title class="font-weight-bold">
          {{ $t('memberDataSection.greenCoffee.addNewQualityGrade') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4 pb-2">
        <v-form ref="form" lazy-validation>
          <v-row dense>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.lotId') }}</label>
              <v-text-field
                v-model="form.lotId"
                outlined
                dense
                hide-details
                placeholder="Enter here"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">
                {{ $t('memberDataSection.greenCoffee.quantity') }}
                <span class="error--text">*</span>
              </label>
              <v-text-field
                v-model.number="form.quantity"
                type="number"
                outlined
                dense
                :rules="rules.quantity"
                placeholder="Enter here"
                @input="computeBags"
              ></v-text-field>
              <span class="text-caption text--secondary">kg</span>
            </v-col>

            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">Unit Size (kg/bag)
                <span class="error--text">*</span>
              </label>
              <v-text-field
                v-model.number="form.unitSize"
                type="number"
                outlined
                dense
                :rules="rules.unitSize"
                placeholder="e.g. 60"
                @input="computeBags"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <p class="text-body-2 mb-1 mt-1">
                {{ $t('memberDataSection.greenCoffee.totalBags') }}: <strong>{{ totalBags }}</strong>
              </p>
              <p class="text-body-2 mb-0">
                {{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}: <strong>{{ leftoverQuantity }} kg</strong>
              </p>
            </v-col>

            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</label>
              <v-select
                v-model="form.qualityScoreId"
                :items="qualityScores"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details
                :placeholder="$t('memberDataSection.pOrder.makeSelection')"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">
                Grade <span class="error--text">*</span>
              </label>
              <v-select
                v-model="form.gradeLevel"
                :items="gradeOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                :rules="rules.gradeLevel"
                placeholder="Select grade"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">
                {{ $t('memberDataSection.greenCoffee.label') }}
                <span class="error--text">*</span>
              </label>
              <v-select
                v-model="form.labelId"
                :items="labels"
                item-text="title"
                item-value="id"
                outlined
                dense
                :rules="rules.labelId"
                :placeholder="$t('memberDataSection.pOrder.makeSelection')"
              ></v-select>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn color="primary" class="white--text" :loading="submitting" @click="submit">{{ $t('submit') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AddQualityGradeModal",
  props: {
    value: { type: Boolean, default: false },
    lot: { type: Object, default: null },
    qualityScores: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    gradeOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: this.value,
      submitting: false,
      form: {
        lotId: "",
        quantity: null,
        unitSize: null,
        qualityScoreId: null,
        gradeLevel: null,
        labelId: null,
      },
      rules: {
        quantity: [
          (v) => (v !== null && v !== "" && v !== undefined) || "Quantity is required",
          (v) => (Number(v) > 0) || "Quantity must be greater than 0",
        ],
        unitSize: [
          (v) => (v !== null && v !== "" && v !== undefined) || "Unit size is required",
          (v) => (Number(v) > 0) || "Unit size must be greater than 0",
        ],
        gradeLevel: [
          (v) => !!v || "Grade is required",
        ],
        labelId: [
          (v) => (v !== null && v !== undefined) || "Label is required",
        ],
      },
    };
  },
  computed: {
    totalBags() {
      const total = Number(this.form.quantity) || 0;
      const unit = Number(this.form.unitSize) || 1;
      return unit ? Math.floor(total / unit) : 0;
    },
    leftoverQuantity() {
      const total = Number(this.form.quantity) || 0;
      const unit = Number(this.form.unitSize) || 1;
      return unit ? total % unit : total;
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) {
        if (this.$refs.form) this.$refs.form.resetValidation();
        const lot = this.lot || {};
        this.form.lotId = lot.lotId || "";
        this.form.quantity = lot.quantity || null;
        this.form.unitSize = lot.unitSize || null;

        const selectedQuality =
          this.qualityScores.find(
            (s) =>
              s.id === lot.qualityScoreId ||
              s.name === lot.qualityTitle ||
              s.name === lot.qualityScore
          ) || null;
        this.form.qualityScoreId = selectedQuality ? selectedQuality.id : null;

        this.form.gradeLevel = lot.qualityScore || null;

        const selectedLabel =
          this.labels.find(
            (l) =>
              l.id === lot.labelId ||
              l.title === lot.label
          ) || null;
        this.form.labelId = selectedLabel ? selectedLabel.id : null;
      }
    },
    visible(v) {
      this.$emit("input", v);
    },
    lot: {
      handler(val) {
        if (val && this.visible) {
          this.form.lotId = val.lotId || "";
          this.form.quantity = val.quantity || null;
          this.form.unitSize = val.unitSize || null;

          const selectedQuality =
            this.qualityScores.find(
              (s) =>
                s.id === val.qualityScoreId ||
                s.name === val.qualityTitle ||
                s.name === val.qualityScore
            ) || null;
          this.form.qualityScoreId = selectedQuality ? selectedQuality.id : null;

          this.form.gradeLevel = val.qualityScore || null;

          const selectedLabel =
            this.labels.find(
              (l) =>
                l.id === val.labelId ||
                l.title === val.label
            ) || null;
          this.form.labelId = selectedLabel ? selectedLabel.id : null;
        }
      },
      immediate: true,
    },
  },
  methods: {
    close() {
      this.visible = false;
      this.$emit("close");
    },
    computeBags() {},
    resetForm() {
      this.form.lotId = "";
      this.form.quantity = null;
      this.form.unitSize = null;
      this.form.qualityScoreId = null;
      this.form.gradeLevel = null;
      this.form.labelId = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.submitting = true;
      try {
        const selectedQuality = this.qualityScores.find((s) => s.id === this.form.qualityScoreId) || {};
        const payload = {
          ...this.form,
          totalBags: this.totalBags,
          leftoverQuantity: this.leftoverQuantity,
          qualityTitle: selectedQuality.name || "",
          qualityScore: this.form.gradeLevel || "",
          label: (this.labels.find((l) => l.id === this.form.labelId) || {}).title || "",
        };
        this.$emit("submit", payload);
        this.resetForm();
        this.close();
        this.$emit("created");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
